/**
 * Тарифные сущности. Сама модель — `TARIFFS_SPEC.md`.
 * Хранение — таблицы `rates_base`, `rates_k1`, `rates_k2`, `idle_settings`
 * (миграция `stryzh/supabase/migrations/20260419120000_tariffs_schema.sql`).
 */
import i18n from '@/utils/i18n'

export type IdleSettingStatus = 'APPRAISAL' | 'PAYMENT_TRANSFER'

export interface RatesBase {
  id: 1
  trip_customer: number
  trip_inspector: number
  inspection_customer: number
  inspection_inspector: number
  deal_customer: number
  deal_inspector: number
  delivery_customer: number
  delivery_inspector: number
  idle_customer: number
  idle_inspector: number
  updated_at: string
}

export type RatesBasePatch = Partial<Omit<RatesBase, 'id' | 'updated_at'>>

export interface RateK1 {
  id: string
  city: string
  coefficient: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface RateK2 {
  id: string
  range_from: number
  /** NULL — открытый сверху диапазон. */
  range_to: number | null
  coefficient: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface IdleSetting {
  status: IdleSettingStatus
  grace_minutes: number
  round_to_minutes: number
  updated_at: string
}

export type ServiceCode = 'trip' | 'inspection' | 'deal' | 'delivery' | 'idle'

/** UI-метаданные по 5 услугам. */
export interface ServiceRow {
  code: ServiceCode
  /** Поля в `rates_base`. */
  customerField: keyof RatesBase
  inspectorField: keyof RatesBase
}

export const SERVICE_ROWS: ServiceRow[] = [
  {
    code: 'trip',
    customerField: 'trip_customer',
    inspectorField: 'trip_inspector',
  },
  {
    code: 'inspection',
    customerField: 'inspection_customer',
    inspectorField: 'inspection_inspector',
  },
  {
    code: 'deal',
    customerField: 'deal_customer',
    inspectorField: 'deal_inspector',
  },
  {
    code: 'delivery',
    customerField: 'delivery_customer',
    inspectorField: 'delivery_inspector',
  },
  {
    code: 'idle',
    customerField: 'idle_customer',
    inspectorField: 'idle_inspector',
  },
]

/**
 * Подпись услуги на текущем языке. i18n-backed: переиспользует существующие
 * `billingService.*` для 4 подтверждаемых услуг, отдельный ключ для простоя.
 * Вызывающий компонент ре-рендерится при смене языка (useTranslation у него).
 */
export function serviceRowLabel(code: ServiceCode): string {
  if (code === 'idle') return i18n.t('tariffs.service.idle')
  return i18n.t(`billingService.${code}`)
}

/** Маппинг 4 «подтверждаемых» услуг на статусы воронки (§1.1 TARIFFS_SPEC.md). */
export const SERVICE_CONFIRMATION: ReadonlyArray<{
  code: Exclude<ServiceCode, 'idle'>
  status: string
}> = [
  { code: 'trip', status: 'DRIVING' },
  { code: 'inspection', status: 'INSPECTION' },
  { code: 'deal', status: 'BUYING' },
  { code: 'delivery', status: 'TRANSPORTATION' },
]

/** Подпись статуса воронки для диаграммы подтверждения услуги. i18n-backed. */
export function serviceConfirmStatusLabel(
  code: Exclude<ServiceCode, 'idle'>,
): string {
  return i18n.t(`tariffs.confirm.status.${code}`)
}
